
import axios from "axios";
import Router, { useRouter } from "next/router";
import { SortKey } from "../components/charts/dataTable";

export async function makeAuthenticatedRequest(
  url, 
  { 
    data = {}, 
    method = 'get', 
    tries = 0, 
    throwError = false, 
    params = {} 
  } = {}) 
{
  if (tries > 2) {
    return {};
  }

  const axiosInstance = axios.create({
    baseURL : '/',
    validateStatus : status => status < 500,
    params
  });
  let res;
  if (method === 'delete') { // axios delete 2nd argument is request options, not request body
    res = await axiosInstance['delete'](url, {
      url,
      data: { ...data }
    });
  } else {
    res = await axiosInstance[method](url, {
      url,
      ...data
    });
  }
  if (res.status === 401) {
    if (await getNewAccessToken()) {
      return makeAuthenticatedRequest(url, { data, method, tries : tries + 1 });
    }
  }
  else if (throwError && [400, 402, 403, 404].includes(res.status)) {
    return new Promise(() => {
      const cognitoErrorMessage = res.data?.message;
      const regularErrorMessage = res.data;
      throw new Error(cognitoErrorMessage ?? regularErrorMessage);
    })
  }
  return res;
};

const getNewAccessToken = async function() {
  const url                      = process.env.NEXT_PUBLIC_AUTH_API_URL + '/browserLogin';
  const res:{ [Key:string]:any } = await axios.post(url, {}, { validateStatus : status => status < 500 });
  if (res.status === 200) {

    return true;
  }
  localStorage.clear();
  Router.push('/login');
  return false;
};

export function isAdmin(userData:{ [ Key : string ]:any }) {
  if (!userData) {
    return false;
  }
  return userData.role && userData.role === 'SuperAdmin';
};

export function isTenantAdmin(userData:{ [ Key : string ]:any }) {
  if (!userData) {
    return false;
  }
  return isAdmin(userData) || userData.role === 'TenantAdmin'
}

export function isAccessTokenCurrent(){ return localStorage.getItem('Expiry') && !((new Date(localStorage.getItem('Expiry')).getTime()) <= Date.now()) };


export function getCityFromAddress(addressString = '') {
  return addressString.split(',').length > 1 ? addressString.split(',')[1].trim() : '';
}

export function getStateFromAddress(addressString = '') {
  return addressString.split(',').length > 2 ? addressString.split(',')[2].trim() : '';
}

export function getStreetFromAddress(addressString = '') {
  return addressString.split(',')[0].trim();
}

export function capitalCase(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export function usePageName() {
  const router = useRouter();
  const pageName = router.pathname.split('/')[router.pathname.split('/').length - 1]

  return pageName;
}

export function certificateBrandingToForm(certificateBranding) {
  return ({
    headerCopy: certificateBranding?.headerCopy || 'Your WiFi installation is complete and has been certified by RouteThis!',
    headerEducationCopy: certificateBranding?.headerEducationCopy || '',
    footerEducationCopy: certificateBranding?.footerEducationCopy || '',
    useDefaultImage: certificateBranding?.useDefaultImage,
    color: certificateBranding?.color || '#3A9680',
    hideSpeedtest: certificateBranding?.hideSpeedtest || false
  })
}

export function handleEnterOrSpaceKeyDown(e, cb) {
  if (e.key === 'Enter' || e.key === ' ' || e.keyCode === 13 || e.keyCode === 49) {
    cb()
  }
}

export const directionAwareSort = (a, b, direction: SortKey['direction']): 0 | 1 | -1 => {
  // if a should be higher in the list than b (less than by asc), we must return -1
  // if a should be lower in the list than b (greater than by asc), we must return 1
  // if a is equal to b, we must return 0
  if (a === b) {
    return 0;
  }    
  else {       
    const aIsGreater = a > b;

    // assume 1, only check for non-1 cases to save extra comparisons     
    if (aIsGreater && direction === 'desc') {
      return -1;
    }
    else if (!aIsGreater && direction === 'asc') {
      return -1;
    }      
    
    return 1;
  }
}

export function tenantDataToForm(tenantDetails) {
  const sharedConfig = tenantDetails.sharedConfigOptions || {};
  return {
    status: tenantDetails.status ?? 'DISABLED',
    TenantName: tenantDetails.id ?? '',
    DisplayName: tenantDetails.displayName ?? '',
    description: tenantDetails.description ?? '',
    smsConfig: (tenantDetails.smsConfig?.body == null && tenantDetails.smsConfig?.from == null) ? (
      {
        body: 'Your WiFi installation is complete and has been certified by [NAME]!\n\nView your certificate here: [LINK]',
        from: '',
      }) : tenantDetails.smsConfig,
    mobileConfig: tenantDetails.mobileConfig,
    routers: Array.isArray(tenantDetails.routers) ? tenantDetails.routers : [],
    meshPods: Array.isArray(tenantDetails.meshPods) ? tenantDetails.meshPods : [],

    customerName: sharedConfig['customerName'] ?? 1,
    routerName: sharedConfig['routerName'] ?? 1,
    levelName: sharedConfig['levelName'] ?? 1,
    podName: sharedConfig['podName'] ?? 1,
    wifiName: sharedConfig['wifiName'] ?? 1,
    areaUnits: sharedConfig['areaUnits'] ?? 2,

    countryCode: sharedConfig['countryCode'] ?? 'CA',
    localeCode: sharedConfig['localeCode'] ?? 'en',
    certifyId: sharedConfig['certifyId'] ?? '',
    resolveId: sharedConfig['resolveId'] ?? '',
    maxPasswordAge: sharedConfig['maxPasswordAge'] ?? 0,
    maxLoginAttempts: sharedConfig['maxLoginAttempts'] ?? 0,
    loginTimeoutSeconds: sharedConfig['loginTimeoutSeconds'] ?? 0,
    maxDaysBetweenLogins: sharedConfig['maxDaysBetweenLogins'] ?? 0,
    region: sharedConfig['region'] ?? 'us-east',
    idleTimeoutSeconds: sharedConfig['idleTimeoutSeconds'] ?? 0,
    numberOfOldPasswords: sharedConfig['numberOfOldPasswords'] ?? 0,
    adminsCanChangeUserPasswords: sharedConfig['adminsCanChangeUserPasswords'] ?? false,
  }
}

export const sidebarTenantFieldsBoxStyling = (sx = {}) =>({ minWidth: '1000px', maxWidth: '1500px', width: '70%', ...sx });